import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Avatar, AvatarFallback } from "../../ui/avatar";
import { Mail, User, Briefcase, Award } from "lucide-react";
import { useTranslation } from "react-i18next";

interface SenderProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  sender: any;
}

export const SenderProfileModal: React.FC<SenderProfileModalProps> = ({
  isOpen,
  onClose,
  sender,
}) => {
  const { t } = useTranslation();

  if (!sender) return null;

  const fullName =
    sender?.displayName ||
    `${sender?.firstName || ""} ${sender?.lastName || ""}`.trim();
  const email = sender?.email;
  const initials =
    (sender?.firstName?.[0] || "") + (sender?.lastName?.[0] || "") ||
    (sender?.displayName?.[0] || "") ||
    (sender?.email?.[0]?.toUpperCase() || "?");

  // Get vendor info if available
  const vendor = sender?.vendor;
  const vendorName = vendor?.displayName || vendor?.name;
  const vendorDescription = vendor?.description;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {t("sender_profile") || "Sender Profile"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Avatar and Name */}
          <div className="flex flex-col items-center space-y-3 text-center">
            <Avatar className="w-20 h-20 ring-4 ring-primary/20">
              <AvatarFallback className="text-2xl font-bold text-white bg-gradient-to-br from-primary to-purple-600">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                {fullName || email}
              </h3>
              {fullName && email && (
                <p className="text-sm text-gray-500">{email}</p>
              )}
            </div>
          </div>

          {/* Information Sections */}
          <div className="space-y-4">
            {/* Title */}
            {sender?.title && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50">
                <Award className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="mb-1 text-xs font-medium text-gray-600">
                    {t("title") || "Title"}
                  </p>
                  <p className="text-sm text-gray-900">{sender.title}</p>
                </div>
              </div>
            )}

            {/* Vendor Information */}
            {vendorName && (
              <div className="flex items-start gap-3 p-3 border rounded-lg bg-gradient-to-br from-purple-50 to-primary/5 border-purple-200/50">
                <Briefcase className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="mb-1 text-xs font-medium text-gray-600">
                    {t("organization") || "Organization"}
                  </p>
                  <p className="mb-1 text-sm font-semibold text-gray-900">
                    {vendorName}
                  </p>
                  {vendorDescription && (
                    <p className="text-xs leading-relaxed text-gray-600">
                      {vendorDescription}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* About Section (if available) */}
            {sender?.bio && (
              <div className="p-4 border border-primary/10 rounded-lg bg-primary/5">
                <h4 className="mb-2 text-sm font-semibold text-gray-900">
                  {t("about") || "About"}
                </h4>
                <div
                  className="text-sm leading-relaxed text-gray-700 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: sender.bio }}
                />
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SenderProfileModal;
